import { useInfiniteQuery } from "@tanstack/react-query";
import axios from "axios";

export const DEFAULT_LIST_PAGE_SIZE = 50;

const resolveHasMore = (items, pageSize, explicitHasMore) => {
  if (items.length < pageSize) {
    return false;
  }

  if (explicitHasMore !== undefined) {
    return explicitHasMore;
  }

  return items.length >= pageSize;
};

/**
 * Shared infinite-query helper for POST list endpoints that accept
 * `{ ...params, paging: { offset, limit } }` and return `{ items, total?, hasMore? }`.
 */
export const useInfiniteListQuery = ({
  queryKey,
  url,
  params = {},
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  enabled = true,
  staleTime,
  keepPreviousData = true,
}) => {
  const query = useInfiniteQuery({
    queryKey,
    enabled: enabled && !!url,
    staleTime,
    keepPreviousData,
    queryFn: async ({ pageParam = 0, signal }) => {
      const response = await axios.post(
        url,
        {
          ...params,
          paging: { offset: pageParam, limit: pageSize },
        },
        { signal, timeout: 60000 }
      );

      const items = response?.data?.items ?? [];
      const total = response?.data?.total ?? items.length;
      const hasMore = resolveHasMore(
        items,
        pageSize,
        response?.data?.hasMore
      );

      return { items, total, hasMore };
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.hasMore) {
        return undefined;
      }

      return allPages.reduce((offset, page) => offset + page.items.length, 0);
    },
  });

  const items = query.data?.pages?.flatMap((page) => page.items) ?? [];
  const total = query.data?.pages?.[0]?.total ?? 0;

  return {
    ...query,
    items,
    total,
  };
};
